import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { FileIcon, InfoIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { Separator } from '@rocco/components/separator';
import { Spinner } from '@rocco/components/spinner';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import { SystemMessageType } from 'generated/graphql';
import { RICH_TEXT_COMPACT_CLASS } from '~/utils/rich-text';
import { localizeTime, useIsSafari } from '~/modules/root';
import { useUserLoggedIn } from '~/modules/user';
import { getAnchorHrefFromEvent, isExternalURL } from '~/modules/url';
import { isImageFile, isPdfFile } from '~/modules/file';
import { isSingleGiphyUrl, getGiphyImageUrl } from '~/modules/giphy';
import type {
    Message,
    MessageAttachment,
    ReplyToMessage,
    TextOrFileMessage,
} from '../../types';
import { useConversation } from '../../context';
import {
    formatDateFull,
    formatDatePartial,
    getChatParticipantsExcludingSelf,
    getEnquiryConversationProfessional,
    getParticipantName,
    getParticipantNames,
    linkifyTextWithMentions,
} from '../../utils';
import { useChatBoxes } from '../../hooks/use-chat-boxes';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';
import { ChatMessageActions } from './ChatMessageActions';
import { ChatMessageAttachments } from './ChatMessageAttachments';
import { ChatMessageImageGrid } from './ChatMessageImageGrid';
import { ChatPDFPreview } from './ChatPDFPreview';
import { ChatMessageReactions } from './ChatMessageReactions';
import { ChatMessageLinkPreviews } from './ChatMessageLinkPreviews';
import { ExternalLinkDialog } from './ExternalLinkDialog';

const TOOLTIP_DELAY_DURATION = 400;

/**
 * A message bubble representing a message in a conversation.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 */
export function ChatMessageBubble({
    message,
    hideSender = false,
    hideActions = false,
    onReact,
    onReply,
    onForward,
    onDelete,
}: {
    /**
     * The conversation message.
     */
    message: Message;
    /**
     * Hide the sender name and avatar. This can be set if the previous message
     * was from the same sender. Defaults to `false`.
     */
    hideSender?: boolean;
    /**
     * Hide the actions for this message bubble. Defaults to `false`.
     */
    hideActions?: boolean;
    /**
     * Callback when a reaction is added or removed.
     */
    onReact?: (message: TextOrFileMessage, reaction: string) => void;
    /**
     * Callback when the reply button is clicked.
     */
    onReply?: (message: TextOrFileMessage) => void;
    /**
     * Callback when the forward button is clicked.
     */
    onForward?: (message: TextOrFileMessage) => void;
    /**
     * Callback when the delete button is clicked.
     */
    onDelete?: () => void;
}) {
    const { conversation, selfParticipant } = useConversation();
    const senderParticipant = message.Participant;
    const sender = senderParticipant.Identity;
    const isOutgoing = senderParticipant.ID === selfParticipant?.ID;
    const senderName = useMemo(() => {
        if (sender.__typename === 'Professional') {
            return message.CreatedBy
                ? `${message.CreatedBy} (${sender.Title})`
                : sender.Title;
        } else if (message.CreatedBy) {
            return message.CreatedBy;
        }
        return sender.Name;
    }, [message.CreatedBy, sender]);

    return (
        <>
            {!hideSender &&
                !isOutgoing &&
                message.__typename !== 'SystemMessage' &&
                message.__typename !== 'RevealContactDetailsMessage' && (
                    <div className="flex w-full items-center gap-[8px]">
                        <ParticipantAvatar
                            className="size-[28px]"
                            fallbackClassName="text-[13px]!"
                            participant={
                                // Show the professional avatar if the sender is a collaborator.
                                senderParticipant.__typename ===
                                    'EnquiryParticipant' &&
                                senderParticipant.Identity.__typename ===
                                    'Member' &&
                                senderParticipant.IsCollaborator &&
                                conversation?.__typename ===
                                    'EnquiryConversation'
                                    ? getEnquiryConversationProfessional(
                                          conversation,
                                      )
                                    : sender
                            }
                        />
                        <span className="wrap-break-word min-w-0 flex-1 text-[12px] font-medium leading-tight">
                            {senderName}
                        </span>
                    </div>
                )}
            <div
                className={cn(
                    'flex w-full flex-col gap-[8px]',
                    isOutgoing ? 'items-end' : 'items-start',
                )}
            >
                {message.__typename === 'TextMessage' && (
                    <>
                        <TextMessageBubble
                            message={message}
                            content={message.Content}
                            replyTo={message.ReplyTo}
                            isHTML={message.IsHTML}
                            isOptimistic={message.isOptimistic ?? false}
                            isOutgoing={isOutgoing}
                            hideActions={hideActions}
                            onReact={onReact}
                            onReply={onReply}
                            onForward={onForward}
                            onDelete={onDelete}
                        />
                        <ChatMessageLinkPreviews
                            className="w-[60%]"
                            message={message}
                            isOutgoing={isOutgoing}
                        />
                        {message.Reactions.length > 0 && (
                            <ChatMessageReactions
                                message={message}
                                isOutgoing={isOutgoing}
                                onReact={onReact}
                            />
                        )}
                    </>
                )}
                {message.__typename === 'FileAttachmentMessage' && (
                    <>
                        <FileAttachmentMessageBubble
                            message={message}
                            content={message.Content}
                            replyTo={message.ReplyTo}
                            attachments={message.Attachments}
                            isHTML={message.IsHTML}
                            isOptimistic={message.isOptimistic ?? false}
                            isOutgoing={isOutgoing}
                            hideActions={hideActions}
                            onReact={onReact}
                            onReply={onReply}
                            onForward={onForward}
                            onDelete={onDelete}
                        />
                        <ChatMessageLinkPreviews
                            className="w-[60%]"
                            message={message}
                            isOutgoing={isOutgoing}
                        />
                        {message.Reactions.length > 0 && (
                            <ChatMessageReactions
                                message={message}
                                isOutgoing={isOutgoing}
                                onReact={onReact}
                            />
                        )}
                    </>
                )}
                {message.__typename === 'DeletedMessage' && (
                    <DeletedMessageBubble
                        message={message}
                        isOutgoing={isOutgoing}
                    />
                )}
                {message.__typename === 'SystemMessage' && (
                    <SystemMessageBubble message={message} />
                )}
                {message.__typename === 'MemberEnquiryMessage' && (
                    <MemberEnquiryMessageBubble
                        message={message}
                        isOutgoing={isOutgoing}
                    />
                )}
                {message.__typename === 'RevealContactDetailsMessage' && (
                    <RevealContactDetailsMessageBubble message={message} />
                )}
            </div>
        </>
    );
}

function TextMessageBubble({
    message,
    content,
    replyTo,
    isHTML,
    isOptimistic,
    isOutgoing,
    hideActions = false,
    onReact,
    onReply,
    onForward,
    onDelete,
}: {
    message: Message;
    content: string;
    replyTo?: ReplyToMessage | null;
    isHTML: boolean;
    isOptimistic: boolean;
    isOutgoing: boolean;
    hideActions?: boolean;
    onReact?: (message: TextOrFileMessage, reaction: string) => void;
    onReply?: (message: TextOrFileMessage) => void;
    onForward?: (message: TextOrFileMessage) => void;
    onDelete?: () => void;
}) {
    const { selfIdentity, mentionLookup } = useConversation();
    const isSafari = useIsSafari();
    const [isExternalLinkWarningOpen, setExternalLinkWarningOpen] =
        useState(false);
    const [pendingExternalUrl, setPendingExternalUrl] = useState<string | null>(
        null,
    );
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);
    const linkifiedContent = useMemo(() => {
        return isHTML
            ? [content]
            : linkifyTextWithMentions({
                  text: content,
                  mentionLookup,
                  linkClassName: 'font-medium underline!',
                  selfIdentity,
              });
    }, [content, isHTML, mentionLookup, selfIdentity]);
    const giphyImageUrl = useMemo(() => {
        const isGiphy = !isHTML && isSingleGiphyUrl(content);
        return isGiphy ? getGiphyImageUrl(content) : null;
    }, [content, isHTML]);

    function handleTextLinkClick(event: React.MouseEvent<HTMLElement>) {
        const href = getAnchorHrefFromEvent(event);
        if (href && isExternalURL(href)) {
            event.preventDefault();
            setPendingExternalUrl(href);
            setExternalLinkWarningOpen(true);
        }
    }

    return (
        <article
            className={cn(
                'group/message flex w-full items-center gap-[4px]',
                isOutgoing ? 'flex-row-reverse' : 'flex-row',
            )}
        >
            <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="wrap-break-word w-fit min-w-[40%] max-w-[80%] space-y-[2px] rounded-[8px] bg-white p-[8px] text-[12px] leading-normal tracking-wide text-black">
                            {replyTo && (
                                <QuotedReply
                                    replyTo={replyTo}
                                    onContentLinkClick={handleTextLinkClick}
                                />
                            )}
                            {giphyImageUrl ? (
                                <img
                                    className="max-h-[200px] max-w-full rounded-[8px] object-contain"
                                    src={giphyImageUrl}
                                    alt="GIF"
                                    loading="lazy"
                                />
                            ) : isHTML ? (
                                <div
                                    className={cn(
                                        RICH_TEXT_COMPACT_CLASS,
                                        'whitespace-pre-wrap',
                                    )}
                                    dangerouslySetInnerHTML={{
                                        __html: content,
                                    }}
                                    onClick={(e) => {
                                        if (!isOutgoing) {
                                            handleTextLinkClick(e);
                                        }
                                    }}
                                ></div>
                            ) : (
                                <div
                                    className="whitespace-pre-wrap"
                                    onClick={(e) => {
                                        if (!isOutgoing) {
                                            handleTextLinkClick(e);
                                        }
                                    }}
                                >
                                    {linkifiedContent}
                                </div>
                            )}
                            <div className="flex items-center justify-end gap-[4px] text-[10px] opacity-70">
                                <time
                                    className="flex-1 truncate text-right"
                                    dateTime={a11yDateString}
                                >
                                    {createdAtString}
                                </time>
                                {isOptimistic && (
                                    <Spinner className="size-[12px] shrink-0" />
                                )}
                            </div>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent side={isOutgoing ? 'right' : 'left'}>
                        {tooltipDateString}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            {!hideActions && (
                <ChatMessageActions
                    className={cn(
                        'opacity-0 group-hover/message:opacity-100',
                        // Transition opacity with hover bugs out on Safari.
                        !isSafari && 'transition-opacity duration-100',
                        // Need to take up space but not be visible.
                        isOptimistic && 'invisible',
                    )}
                    message={message}
                    isOutgoing={isOutgoing}
                    onReact={(reaction) => {
                        if (
                            message.__typename === 'TextMessage' ||
                            message.__typename === 'FileAttachmentMessage'
                        ) {
                            onReact?.(message, reaction);
                        }
                    }}
                    onReply={() => {
                        if (
                            message.__typename === 'TextMessage' ||
                            message.__typename === 'FileAttachmentMessage'
                        ) {
                            onReply?.(message);
                        }
                    }}
                    onForward={() => {
                        if (
                            message.__typename === 'TextMessage' ||
                            message.__typename === 'FileAttachmentMessage'
                        ) {
                            onForward?.(message);
                        }
                    }}
                    onDelete={onDelete}
                />
            )}
            {pendingExternalUrl && (
                <ExternalLinkDialog
                    open={isExternalLinkWarningOpen}
                    url={pendingExternalUrl}
                    onOpenChange={setExternalLinkWarningOpen}
                    onConfirm={() => setPendingExternalUrl(null)}
                    onCancel={() => setPendingExternalUrl(null)}
                />
            )}
        </article>
    );
}

/**
 * A quoted reply representing a message being replied to or forwarded.
 */
function QuotedReply({
    replyTo,
    onContentLinkClick,
}: {
    replyTo: ReplyToMessage;
    onContentLinkClick?: (event: React.MouseEvent<HTMLElement>) => void;
}) {
    const { conversationID, selfIdentity, mentionLookup } = useConversation();
    const createdAt = useMemo(() => {
        return localizeTime(replyTo.Created);
    }, [replyTo.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);
    const senderName = useMemo(() => {
        return getParticipantName(replyTo.Participant);
    }, [replyTo.Participant]);
    const linkifiedContent = useMemo(() => {
        return replyTo.IsHTML
            ? [replyTo.Content]
            : linkifyTextWithMentions({
                  text: replyTo.Content,
                  mentionLookup,
                  linkClassName: 'font-medium underline!',
                  selfIdentity,
                  renderUnknownMentions: true,
              });
    }, [replyTo, mentionLookup, selfIdentity]);
    const attachments =
        replyTo.__typename === 'FileAttachmentMessage'
            ? replyTo.Attachments
            : null;

    return (
        <blockquote
            className={cn(
                'mb-[6px] border-l-2 pl-[6px] text-[11px] leading-normal',
                'border-l-black/30 text-black/70',
            )}
        >
            <cite className="font-medium not-italic">{senderName}</cite>
            {replyTo.Content &&
                (replyTo.IsHTML ? (
                    <div
                        className={cn(
                            RICH_TEXT_COMPACT_CLASS,
                            'whitespace-pre-wrap',
                        )}
                        dangerouslySetInnerHTML={{ __html: replyTo.Content }}
                        onClick={onContentLinkClick}
                    />
                ) : (
                    <div
                        className="whitespace-pre-wrap"
                        onClick={onContentLinkClick}
                    >
                        {linkifiedContent}
                    </div>
                ))}
            {attachments && attachments.length > 0 && (
                <ul className="mt-[4px] flex flex-col gap-[4px]">
                    {attachments.map((attachment) => (
                        <li key={attachment.ID} className="w-full">
                            <a
                                className="flex items-center gap-[6px] rounded-[4px] bg-black/5 p-[4px] hover:bg-black/10"
                                href={attachment.Link}
                                target="_blank"
                                rel="noopener"
                                download
                            >
                                <FileIcon className="size-[14px] shrink-0" />
                                <span className="truncate">
                                    {attachment.FileName}
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
            )}
            {/* Show timestamp only if forwarded message (not the same conversation). */}
            {conversationID > 0 &&
                replyTo.ConversationID !== conversationID && (
                    <div className="flex items-center justify-end text-[10px]">
                        <time
                            className="truncate text-right"
                            dateTime={a11yDateString}
                        >
                            Forwarded &middot; {createdAtString}
                        </time>
                    </div>
                )}
        </blockquote>
    );
}

function FileAttachmentMessageBubble({
    message,
    content,
    replyTo,
    attachments,
    isHTML,
    isOptimistic,
    isOutgoing,
    hideActions = false,
    onReact,
    onReply,
    onForward,
    onDelete,
}: {
    message: Message;
    content?: string | null;
    replyTo?: ReplyToMessage | null;
    attachments: MessageAttachment[];
    isHTML: boolean;
    isOptimistic: boolean;
    isOutgoing: boolean;
    hideActions?: boolean;
    onReact?: (message: TextOrFileMessage, reaction: string) => void;
    onReply?: (message: TextOrFileMessage) => void;
    onForward?: (message: TextOrFileMessage) => void;
    onDelete?: () => void;
}) {
    const isSafari = useIsSafari();
    const singlePDF =
        attachments.length === 1 && isPdfFile(attachments[0]?.FileName)
            ? attachments[0]
            : undefined;
    const isAllImages =
        attachments.length > 0 &&
        attachments.every((att) => isImageFile(att.FileName));
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);

    return (
        <>
            {(!!content || !!replyTo) && (
                <TextMessageBubble
                    message={message}
                    content={content ?? ''}
                    replyTo={replyTo}
                    isHTML={isHTML}
                    isOptimistic={isOptimistic}
                    isOutgoing={isOutgoing}
                    hideActions={hideActions}
                    onReact={onReact}
                    onReply={onReply}
                    onForward={onForward}
                    onDelete={onDelete}
                />
            )}
            {attachments.length > 0 && (
                <article
                    className={cn(
                        'group/message flex w-full items-center gap-[4px]',
                        isOutgoing ? 'flex-row-reverse' : 'flex-row',
                    )}
                >
                    {singlePDF ? (
                        <ChatPDFPreview
                            className="w-[70%]"
                            attachment={singlePDF}
                            date={createdAt}
                            isOptimistic={isOptimistic}
                        />
                    ) : isAllImages ? (
                        <ChatMessageImageGrid
                            className="w-[80%]"
                            images={attachments}
                            date={createdAt}
                            isOutgoing={isOutgoing}
                            isOptimistic={isOptimistic}
                        />
                    ) : (
                        <ChatMessageAttachments
                            className="w-[60%]"
                            attachments={attachments}
                            date={createdAt}
                            isOutgoing={isOutgoing}
                            isOptimistic={isOptimistic}
                            tooltipDelayMs={TOOLTIP_DELAY_DURATION}
                        />
                    )}
                    {!hideActions && (
                        <ChatMessageActions
                            className={cn(
                                'opacity-0 group-hover/message:opacity-100',
                                // Transition opacity with hover bugs out on Safari.
                                !isSafari && 'transition-opacity duration-100',
                                // Need to take up space but not be visible.
                                isOptimistic && 'invisible',
                            )}
                            message={message}
                            isOutgoing={isOutgoing}
                            onReact={(reaction) => {
                                if (
                                    message.__typename === 'TextMessage' ||
                                    message.__typename ===
                                        'FileAttachmentMessage'
                                ) {
                                    onReact?.(message, reaction);
                                }
                            }}
                            onReply={() => {
                                if (
                                    message.__typename === 'TextMessage' ||
                                    message.__typename ===
                                        'FileAttachmentMessage'
                                ) {
                                    onReply?.(message);
                                }
                            }}
                            onForward={() => {
                                if (
                                    message.__typename === 'TextMessage' ||
                                    message.__typename ===
                                        'FileAttachmentMessage'
                                ) {
                                    onForward?.(message);
                                }
                            }}
                            onDelete={onDelete}
                        />
                    )}
                </article>
            )}
        </>
    );
}

function DeletedMessageBubble({
    message,
    isOutgoing,
}: {
    message: Extract<Message, { __typename: 'DeletedMessage' }>;
    isOutgoing: boolean;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <article className="wrap-break-word w-fit min-w-[40%] max-w-[80%] rounded-[8px] bg-white p-[8px] text-[12px] italic leading-normal tracking-wide text-black">
                        This message has been deleted
                    </article>
                </TooltipTrigger>
                <TooltipContent side={isOutgoing ? 'right' : 'left'}>
                    {tooltipDateString}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function SystemMessageBubble({
    message,
}: {
    message: Extract<Message, { __typename: 'SystemMessage' }>;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const { professionalID } = useConversation();
    const { openChatBox } = useChatBoxes();
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const senderName = useMemo(() => {
        const sender = message.Participant.Identity;
        if (sender.__typename === 'Professional') {
            return message.CreatedBy
                ? `${message.CreatedBy} (${sender.Title})`
                : sender.Title;
        } else if (message.CreatedBy) {
            return message.CreatedBy;
        }
        return sender.Name;
    }, [message]);
    const messageText = useMemo(() => {
        if (message.Type === SystemMessageType.NameChanged) {
            if (message.Value) {
                return (
                    <>
                        <strong>{senderName}</strong> named this conversation{' '}
                        <strong>"{message.Value}"</strong>
                    </>
                );
            }
            return (
                <>
                    <strong>{senderName}</strong> removed the conversation title
                </>
            );
        } else if (message.Type === SystemMessageType.BuildHubChanged) {
            if (message.ValueBuildHub) {
                return (
                    <>
                        <strong>{senderName}</strong> updated the project to{' '}
                        <strong>"{message.ValueBuildHub.Title}"</strong>
                    </>
                );
            }
            return (
                <>
                    <strong>{senderName}</strong> removed the linked project
                </>
            );
        } else if (message.Type === SystemMessageType.ThumbnailChanged) {
            if (message.ValueID > 0) {
                return (
                    <>
                        <strong>{senderName}</strong> updated the chat thumbnail
                    </>
                );
            }
            return (
                <>
                    <strong>{senderName}</strong> removed the chat thumbnail
                </>
            );
        } else if (message.Type === SystemMessageType.ParticipantAdded) {
            const addedParticipant = message.ValueParticipant?.Identity;
            return (
                <>
                    <strong>{senderName}</strong> added{' '}
                    <strong>
                        {addedParticipant
                            ? getParticipantName(addedParticipant)
                            : 'a user'}
                    </strong>{' '}
                    to this conversation
                </>
            );
        } else if (message.Type === SystemMessageType.ParticipantRemoved) {
            if (message.Participant.ID === message.ValueParticipant?.ID) {
                return (
                    <>
                        <strong>{senderName}</strong> left this conversation
                    </>
                );
            }
            const removedParticipant = message.ValueParticipant?.Identity;
            return (
                <>
                    <strong>{senderName}</strong> removed{' '}
                    <strong>
                        {removedParticipant
                            ? getParticipantName(removedParticipant)
                            : 'a user'}
                    </strong>{' '}
                    from this conversation
                </>
            );
        } else if (message.Type === SystemMessageType.MessagesMovedOut) {
            const targetChat = message.ValueConversation;
            const otherParticipants = getChatParticipantsExcludingSelf(
                targetChat,
                isLoggedIn ? user.ID : 0,
                professionalID,
            );
            return (
                <>
                    <strong>{senderName}</strong> moved some of the messages
                    from this conversation to{' '}
                    {targetChat ? (
                        <>
                            {!targetChat.Name && 'one with '}
                            <strong
                                className="cursor-pointer underline"
                                onClick={() => {
                                    if (targetChat) {
                                        openChatBox(
                                            Number(targetChat.ID),
                                            professionalID,
                                        );
                                    }
                                }}
                            >
                                {targetChat.Name ??
                                    getParticipantNames(otherParticipants, 3)}
                            </strong>
                        </>
                    ) : (
                        'another one'
                    )}
                </>
            );
        } else if (message.Type === SystemMessageType.MessagesMovedIn) {
            return (
                <>
                    <strong>{senderName}</strong> moved messages from a previous
                    conversation to this one
                </>
            );
        }
        console.warn('Unknown system message type', message.Type);
        return <></>;
    }, [message, senderName, user.ID, isLoggedIn, professionalID, openChatBox]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION * 3}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="wrap-break-word w-full px-[8px] py-[4px] text-center text-[12px] leading-normal tracking-wide text-gray-800">
                        {messageText}
                    </div>
                </TooltipTrigger>
                <TooltipContent side="top">{tooltipDateString}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function MemberEnquiryMessageBubble({
    message,
    isOutgoing,
}: {
    message: Extract<Message, { __typename: 'MemberEnquiryMessage' }>;
    isOutgoing: boolean;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);
    const items = useMemo(() => {
        return message.LibraryItems.filter(
            (item) => item.__typename !== 'Professional',
        );
    }, [message.LibraryItems]);

    // TODO: Tracker: CommentLibraryItems.tsx

    return items.length ? (
        <>
            <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <article className="wrap-break-word w-fit min-w-[60%] max-w-[80%] overflow-hidden rounded-[8px] bg-white text-[12px] leading-normal tracking-wide text-black">
                            {items.map(
                                ({
                                    ID,
                                    Title,
                                    Link: link,
                                    Professional,
                                    LibraryThumb,
                                }) => (
                                    <React.Fragment key={ID}>
                                        <Link
                                            to={link}
                                            className="block w-full p-[8px] leading-tight hover:bg-black/10"
                                        >
                                            {!!LibraryThumb?.ThumbImageFile
                                                ?.Thumbnail.Desktop && (
                                                <Image
                                                    className="mb-[12px] w-[140px]"
                                                    src={
                                                        LibraryThumb
                                                            .ThumbImageFile
                                                            .Thumbnail.Desktop
                                                    }
                                                    alt={Title}
                                                    fit="contain"
                                                    fadeIn={false}
                                                />
                                            )}
                                            <div className="mb-[2px] font-medium">
                                                {Title}
                                            </div>
                                            <div className="opacity-70">
                                                {Professional?.Title}
                                            </div>
                                        </Link>
                                        <div className="px-[8px]">
                                            <Separator className="bg-black/10" />
                                        </div>
                                    </React.Fragment>
                                ),
                            )}
                            <div className="space-y-[2px] p-[8px]">
                                <div className="whitespace-pre-wrap">
                                    {message.Content}
                                </div>
                                <time
                                    className="block flex-1 truncate text-right text-[10px] opacity-70"
                                    dateTime={a11yDateString}
                                >
                                    {createdAtString}
                                </time>
                            </div>
                        </article>
                    </TooltipTrigger>
                    <TooltipContent side={isOutgoing ? 'right' : 'left'}>
                        {tooltipDateString}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            {message.Attachments.length > 0 && (
                <FileAttachmentMessageBubble
                    message={message}
                    content={undefined}
                    attachments={message.Attachments}
                    isHTML={false}
                    isOptimistic={message.isOptimistic ?? false}
                    isOutgoing={isOutgoing}
                />
            )}
        </>
    ) : (
        <FileAttachmentMessageBubble
            message={message}
            content={message.Content}
            attachments={message.Attachments}
            isHTML={false}
            isOptimistic={message.isOptimistic ?? false}
            isOutgoing={isOutgoing}
        />
    );
}

function RevealContactDetailsMessageBubble({
    message,
}: {
    message: Extract<Message, { __typename: 'RevealContactDetailsMessage' }>;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="w-full space-y-[2px] rounded-[8px] bg-white p-[8px] leading-normal tracking-wide text-black">
                        <div className="wrap-break-word flex items-center gap-[8px]">
                            <InfoIcon className="size-[16px]" />
                            <div className="min-w-0 flex-1 text-[12px]">
                                {message.Content}
                            </div>
                        </div>
                        <time
                            className="block truncate text-right text-[10px] opacity-70"
                            dateTime={a11yDateString}
                        >
                            {createdAtString}
                        </time>
                    </div>
                </TooltipTrigger>
                <TooltipContent side="top">{tooltipDateString}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
